/**
 * anchors.mjs — something to hold onto, for the five chapters that stopped offering one.
 *
 * FOUNDER, 2026-08-15: "ensure course is world class and not paper and texty" → "then do the
 * anchors".
 *
 * ── THE MEASUREMENT THAT PROMPTED THIS ────────────────────────────────────────────────────────
 * Counted across both courses on 2026-08-15: everyday anchors per 1,000 words.
 *
 *     Code101 ch1 ......... 24.7   (recipe, ATM, WhatsApp, washing machine)
 *     Code101 average ..... 11.3
 *     CodeAI101 ch1-2 ..... 1.0 - 1.8
 *     CodeAI101 ch3-7 ..... 0.0 - 0.6      ← chapter 3 has none at all
 *
 * The prose in those five chapters is correct, plain and well-built — grade 8.2, every technical
 * term defined at first use. It is not hard reading. It is reading with nothing to hold onto, and
 * that is a different problem with a different fix.
 *
 * ── WHY THESE LIVE IN THE LABS AND NOT IN HIS MODULES ─────────────────────────────────────────
 * The module prose is the founder's authorship. Adding an analogy to a paragraph he wrote would be
 * me editing his course, which is not mine to do and has never been how this works. So the anchors
 * are an ADDITIVE layer the instruments carry: his text is untouched, and the concrete thing a
 * learner needs arrives at the moment they are about to use it. If he later wants them in the
 * prose, they are drafted and waiting.
 *
 * ── TWO REGISTERS, BECAUSE THE AUDIENCE IS TWO AUDIENCES ──────────────────────────────────────
 * He said "a mariner or layman". A mariner reads a ship's telegraph instantly and a farmer does
 * not; a farmer reads a phoned-in food order instantly and both read a washing machine. So every
 * anchor comes in both, and the learner is shown both rather than being guessed at.
 *
 * ── AN ANCHOR IS NOT AN ARGUMENT ──────────────────────────────────────────────────────────────
 * Each one carries `holds` — the single thing the comparison is true about — and `breaks` — where
 * it stops being true. An analogy without a stated limit is a way of being wrong later, and a
 * course that teaches "confident-but-wrong" as a failure mode cannot ship one.
 */

/** Measured 2026-08-15. gate-anchors.mjs re-measures the modules and fails if this drifts. */
export const THE_GAP = Object.freeze({
  measured_on: '2026-08-15',
  method: 'everyday-object references per 1,000 words, counted across each module\'s prose',
  code101_ch1: 24.7,
  codeai101_ch3_to_7: Object.freeze([0.0, 1.8, 0.4, 0.4, 0.4, 0.6]),
  finding: 'The prose in CodeAI101 chapters 3 to 7 is correct and plain — grade 8.2, every term defined at first use. What it stopped doing was reaching for things a reader already knows.',
});

/**
 * One anchor per chapter, in both registers, each with what it holds and where it breaks.
 * `chapter` is the CodeAI101 chapter it belongs to.
 */
export const ANCHORS = Object.freeze([
  {
    chapter: 3, id: 'telegraph',
    sea: {
      thing: 'the engine room telegraph',
      says: 'The bridge rings FULL AHEAD. The bridge does not turn the shaft. Down in the engine room somebody reads the ring and opens the throttle, and until they do, nothing moves.',
    },
    shore: {
      thing: 'phoning in a food order',
      says: 'You say what you want. Somebody else has to actually cook it. Saying it and it happening are two different events, with a person in between.',
    },
    holds: 'The AI proposes a tool call; a separate harness is what actually executes it. Those are two events, and everything about permissions lives in the gap between them.',
    breaks: 'A telegraph always has a human at both ends. An auto-approving harness has nobody at the receiving end — which is exactly what makes it fast and exactly what makes it dangerous.',
  },
  {
    chapter: 4, id: 'nightorders',
    sea: {
      thing: 'standing orders versus the night order book',
      says: 'Call me if visibility drops — that is one instruction, and you wait to be woken. Night orders are different: they run the whole watch without you, and you find out in the morning what was done.',
    },
    shore: {
      thing: 'washing by hand versus a machine cycle',
      says: 'By hand, every step is you. You set a cycle and walk away, and it keeps going without you until it stops or something goes wrong.',
    },
    holds: 'A prompt asks once and stops; an agent keeps going until it thinks it is done. The difference is who drives the iteration.',
    breaks: 'A washing machine cannot decide to wash something you did not put in it. An agent can decide to take a step you never imagined, which is why the boundaries in this chapter exist.',
  },
  {
    chapter: 5, id: 'chartroom',
    sea: {
      thing: 'the chartroom',
      says: 'No navigator memorises every port on earth. You work out where you are going, then pull the right chart and the right pilot book for that passage — and you can show anyone which volume you took the answer from.',
    },
    shore: {
      thing: 'a recipe you look up',
      says: 'Cooking from memory gives you roughly the dish. Opening the recipe for THIS dish, at the moment you need it, gives you the actual quantities — and you can point at the page.',
    },
    holds: 'Retrieve the few relevant documents at the moment of the question, put them in front of the model, and cite which ones. Search, paste, ask.',
    breaks: 'A navigator knows which chart is the right one. Retrieval has to work that out by arithmetic, and when it gets it wrong the model answers confidently from the wrong chart.',
  },
  {
    chapter: 6, id: 'certificates',
    sea: {
      thing: 'a ship\'s certificates',
      says: 'A Safety Equipment Certificate does not say the ship is safe. It says what was inspected, by whom, on what date, valid until when. A port state officer reads the certificate — they do not read the brochure.',
    },
    shore: {
      thing: 'the batch and expiry printed on a medicine strip',
      says: 'Not "we make quality medicines". A batch number and a date, checkable by anyone holding the strip, against a record kept somewhere else.',
    },
    holds: 'A declaration states what a system holds, what it can do, what it emits and what evidence it produces — in a form a stranger can check without asking anyone.',
    breaks: 'A certificate is issued once and expires. A Forja declaration is meant to be true continuously and re-checked mechanically, which is a harder promise than any piece of paper makes.',
  },
  {
    chapter: 7, id: 'checklist',
    sea: {
      thing: 'the pre-departure checklist',
      says: 'Not everything the ship could possibly have. The minimum that means she may legally and safely leave the berth — and every item on it is there because a ship once sailed without it.',
    },
    shore: {
      thing: 'what you must carry to drive legally',
      says: 'Licence, registration, insurance. Not a good stereo. A short list you cannot be short of, and a long list of things that can wait.',
    },
    holds: 'A v0 is the smallest system that has all seven requirements, even narrowly. Everything else is roadmap, and each roadmap item has to earn its place.',
    breaks: 'A checklist is fixed by regulation. The seven are a judgement about what makes an AI product honest, and the temptation this chapter warns about is precisely the belief that your project is the exception.',
  },
]);

export const anchorFor = (chapter) => ANCHORS.find((a) => a.chapter === Number(chapter)) || null;

/** Every anchor must declare where it fails. Checked by the gate, not by hope. */
export const allDeclareTheirLimit = () => ANCHORS.every((a) => Boolean(a.holds) && Boolean(a.breaks));

export const WHY_TWO = 'A mariner reads a telegraph instantly and a farmer does not; a farmer reads a phoned-in order instantly and both read a washing machine. Both are given, rather than guessing which reader arrived.';
export const WHY_A_LIMIT = 'Every anchor says where it stops being true. An analogy without a stated limit is a way of being wrong later, and this course teaches confident-but-wrong as a failure mode.';
